import { Component } from '@paragon/contract-library';
import { degreesToSlope, Direction2D, Line2D, Point2D, slopeToDegrees } from '@paragon/geometry-library';
import { findAndCalculateLine } from '@paragon/relative-geometry';
import { assertExhaustiveSwitch, roundToIncrement } from '@paragon/utilities';

import { useAppSelector } from '../../components/hooks';
import { snapAngleToDegreesWithOrthogonalBias } from '../../library/layout-snapping/point-snapping/angleSnapping';
import { BasicInsertionInformation, getBasicInsertionInformation } from '../../store/selectors/basicInsertionSelectors';
import { getActiveComponentDesign } from '../../store/selectors/getActiveComponentDesignSelector';
import {
  IntersectionSelection,
  LineSelection,
  ReferenceLineIntersection,
  SnappingLevel,
} from '../../store/slices/applicationSlice';
import { useMousePositionInScene } from './useMousePositionInScene';

/** Returns the increment to which a distance should be rounded at the given snapping level, in inches. */
export function getDistanceSnap(snappingLevel: SnappingLevel) {
  switch (snappingLevel) {
    case 'coarse':
      return 12;

    case 'fine':
      return 1 / 4;

    case 'default':
      return 1;

    default:
      assertExhaustiveSwitch(snappingLevel);
  }
}

// The maximum slope allowed.
export const SLOPE_CAP = 40;

// The tolerance allowed (for ease of mouse input) over SLOPE_CAP before the line will snap to 90°.
const SLOPE_TOLERANCE = 10;

function snapPositionForParallelLineInsertion(
  base: LineSelection,
  component: Component,
  mousePosition: Point2D,
  snappingLevel: SnappingLevel,
): Point2D | null {
  const baseLine = findAndCalculateLine(base.value, component);
  if (baseLine == null) return null;

  const perpendicularLine = new Line2D(mousePosition.projectOntoLine(baseLine), baseLine.direction.rotateQuarterTurn());

  const perpendicularDistance = mousePosition.signedDistanceAlong(perpendicularLine);
  const snappedDistance = roundToIncrement(perpendicularDistance, getDistanceSnap(snappingLevel));

  return perpendicularLine.getPointAlongLine(snappedDistance);
}

function snapPositionForPointSlopeLineInsertion(base: IntersectionSelection, mousePosition: Point2D): Point2D {
  const intersectionPoint = Point2D.fromObject(base.value.point);
  const currentDirection = Direction2D.fromPoints(intersectionPoint, mousePosition);
  const currentAngle = Direction2D.right.angleTo(currentDirection);

  const closestDegree = snapAngleToDegreesWithOrthogonalBias(currentAngle);

  // Calculate the closest integer pitch snap, except for values over the cap which should snap to 90°.
  let closestIntegerPitchAngle;
  const snappedSlope = Math.round(degreesToSlope(currentAngle));
  if (Math.abs(snappedSlope) > SLOPE_CAP + SLOPE_TOLERANCE) {
    closestIntegerPitchAngle = 90;
  } else {
    closestIntegerPitchAngle = slopeToDegrees(Math.min(SLOPE_CAP, snappedSlope));

    // Slopes in Quadrant II and III aren't possible, so fix that if needed.
    if (currentAngle > 90 || currentAngle < -90) closestIntegerPitchAngle += 180;
  }

  // Choose the closer snap, either degrees or pitch
  const closestDegreeDirection = Direction2D.fromAngle(closestDegree);
  const closestIntegerPitchDirection = Direction2D.fromAngle(closestIntegerPitchAngle);
  const differenceToClosestDegree = currentDirection.angleBetween(closestDegreeDirection);
  const differenceToClosestPitch = currentDirection.angleBetween(closestIntegerPitchDirection);
  const snappedDirection =
    differenceToClosestDegree < differenceToClosestPitch ? closestDegreeDirection : closestIntegerPitchDirection;

  const snappedLine = new Line2D(intersectionPoint, snappedDirection);

  return snappedLine.getPointAlongLine(intersectionPoint.distanceTo(mousePosition));
}

export function snapMousePositionDuringBasicInsertion(
  rawPosition: Point2D | null,
  component: Component,
  insertionInfo: BasicInsertionInformation,
  hoveredIntersection: ReferenceLineIntersection | null,
): Point2D | null {
  if (rawPosition === null || !insertionInfo.inserting) return null;

  if (insertionInfo.definition.firstSelection?.type === 'line' && !hoveredIntersection) {
    return snapPositionForParallelLineInsertion(
      insertionInfo.definition.firstSelection,
      component,
      rawPosition,
      insertionInfo.snappingLevel,
    );
  }

  if (insertionInfo.definition.firstSelection?.type === 'intersection') {
    // For point-to-point lines -- note that this will only be used for the preview line.  Actual insertion of
    //   point-to-point lines is triggered by the event system so snapping doesn't matter there.
    if (hoveredIntersection) return Point2D.fromObject(hoveredIntersection.point);

    return snapPositionForPointSlopeLineInsertion(insertionInfo.definition.firstSelection, rawPosition);
  }

  return rawPosition;
}

export function useMousePositionWithBasicInsertionSnapping() {
  const rawPosition = useMousePositionInScene();
  const rawPosition2D = rawPosition?.projectTo2D() ?? null;

  const basicInsertionInfo = useAppSelector(getBasicInsertionInformation);

  const componentDesign = useAppSelector(getActiveComponentDesign);
  if (componentDesign?.component == null) return null;

  return snapMousePositionDuringBasicInsertion(rawPosition2D, componentDesign.component, basicInsertionInfo, null);
}
